/**
 * @file ConsentPreviewBanner.tsx
 * @description Образец баннера cookie в кабинете платформы (phase19.16,
 * Р3г; uiux-r3g-consent-texts §3, §6): полоса, карточка или компактная
 * полоса — та же раскладка, что у витрины (`ConsentBannerView`), но на
 * нейтральных цветах: цвета задаёт магазин (SB120). Кнопки равнозначные,
 * по 48 px; переполнение на 360 видно до публикации.
 *
 * Раскладка зависит от ширины РАМКИ образца (контейнерные запросы), а не
 * окна: «360» на широком экране показывает телефонную раскладку.
 *
 * Кнопки — как на витрине: три в ряд, длинная подпись переносится внутри
 * кнопки (`min-w-0`, `whitespace-normal`, `break-words`), а не обрезается
 * (дефект QA D2: «Только / еобходимы» на 360).
 */

import type { ConsentBannerTexts, SiteConsentBannerKind } from "@chaiberi/shared";

import { previewBannerText, type PreviewCategory } from "../../../lib/platform/consent-preview";
import { PREVIEW_BUTTONS, type PreviewForm } from "./preview-options";
import { cn } from "@/lib/utils";

interface Props {
  texts: ConsentBannerTexts;
  view: SiteConsentBannerKind;
  form: Exclude<PreviewForm, "settings">;
  categories: readonly PreviewCategory[];
}

/** Кнопка образца — не нажимается: это картинка, а не элемент управления. */
function SampleButton({ label, wide }: { label: string; wide?: boolean }) {
  return (
    <span
      data-preview-button=""
      className={cn(
        "inline-flex min-h-12 min-w-0 items-center justify-center whitespace-normal break-words rounded-lg bg-on-surface px-2 py-1 text-center text-sm leading-tight text-surface-lowest",
        wide ? "w-full px-6 @min-[40rem]:w-auto" : "w-full",
      )}
    >
      {label}
    </span>
  );
}

export function ConsentPreviewBanner({ texts, view, form, categories }: Props) {
  const built = previewBannerText(texts, view, categories);
  const compact = form === "compact";
  const card = form === "card";
  const info = view === "info";
  const buttons = PREVIEW_BUTTONS[view];

  return (
    <div className={cn("flex", card ? "justify-end p-4" : "")}>
      <div
        data-preview-form={form}
        className={cn(
          "bg-surface-lowest text-on-surface [box-shadow:inset_0_0_0_1px_var(--outline-variant)]",
          card ? "flex w-full max-w-[22.5rem] flex-col gap-3 rounded-2xl p-4" : cn("w-full px-4", compact ? "py-2" : "py-3"),
        )}
      >
        <div
          className={cn(
            "flex flex-col gap-2",
            !card && "@min-[40rem]:flex-row @min-[40rem]:items-center @min-[40rem]:justify-between @min-[40rem]:gap-4",
          )}
        >
          <p className={cn("m-0", compact ? "line-clamp-2 text-xs" : info ? "text-xs" : "text-sm")}>
            {compact ? built.compactText : built.text} <span className="underline">{texts.more}</span>
          </p>
          <div
            className={cn(
              "grid gap-2",
              card || info ? "grid-cols-1" : "grid-cols-3 @min-[40rem]:w-[30rem] @min-[40rem]:shrink-0",
              info && !card && "@min-[40rem]:w-auto",
            )}
          >
            {buttons.map((button) => (
              <SampleButton key={button} label={texts.buttons[button]} wide={info && !card} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
